import { useState, type FormEvent } from 'react';
import type { Product, ProductFormData } from '../types';
import { useProductStore } from '../stores/productStore';
import { useApp } from '../../components/AppProvider';
import { servicesTranslations } from '../i18n/translations/services';
import { slugify } from '../utils/string';

/**
 * Logica di submit/validazione per ProductQuickForm (Task 7 — tab Articoli
 * in ServicesPage). Estratto in hook a parte per rispettare il limite di
 * dimensione dei componenti (CLAUDE.md: max ~250 righe) tenendo
 * ProductQuickForm.tsx puramente di presentazione.
 *
 * Defaults quick-create: gli stessi campi obbligatori reali di
 * ProductFormData (status/type/backorders/virtual/downloadable/featured/
 * visibility/tax_status/category_ids/tag_ids/attributes/attributeGroups/
 * variations) — verificati leggendo productStore.ts createProduct/
 * updateProduct: entrambi fanno spread selettivo (solo le relazioni
 * category_ids/tag_ids/attributes/attributeGroups/variations/images/files
 * vengono estratte a parte), quindi ogni altro campo passato in
 * ProductFormData (inclusi vat_rate, regular_price, cost_price,
 * main_image_url) arriva così com'è alla insert/update su `products`.
 */
export const VAT_RATE_OPTIONS = [22, 10, 5, 4, 0];

export interface ProductQuickFormValues {
  name: string;
  sku: string;
  short_description: string;
  category_id: string;
  regular_price: string;
  cost_price: string;
  vat_rate: number;
  main_image_url: string;
  manage_stock: boolean;
  stock_quantity: string;
  low_stock_threshold: string;
}

type FormErrors = Partial<Record<keyof ProductQuickFormValues, string>>;

function buildInitialValues(product?: Product): ProductQuickFormValues {
  return {
    name: product?.name ?? '',
    sku: product?.sku ?? '',
    short_description: product?.short_description ?? '',
    category_id: product?.categories?.[0]?.id ?? '',
    regular_price: product?.regular_price != null ? String(product.regular_price) : '',
    cost_price: product?.cost_price != null ? String(product.cost_price) : '',
    vat_rate: product?.vat_rate ?? 22,
    main_image_url: product?.main_image_url ?? '',
    manage_stock: product?.manage_stock ?? false,
    stock_quantity: product?.stock_quantity != null ? String(product.stock_quantity) : '0',
    low_stock_threshold: product?.low_stock_threshold != null ? String(product.low_stock_threshold) : '0',
  };
}

interface UseProductQuickFormParams {
  product?: Product;
  warehouseActive: boolean;
  onSaved: (product: Product) => void;
}

export function useProductQuickForm({ product, warehouseActive, onSaved }: UseProductQuickFormParams) {
  const { createProduct, updateProduct } = useProductStore();
  const { toast, language } = useApp();
  const t = servicesTranslations[language as keyof typeof servicesTranslations] || servicesTranslations.en;

  const [values, setValues] = useState<ProductQuickFormValues>(() => buildInitialValues(product));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const setField = <K extends keyof ProductQuickFormValues>(field: K, value: ProductQuickFormValues[K]) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!values.name.trim()) nextErrors.name = t.articleNameRequired;
    if (values.regular_price.trim() === '' || Number(values.regular_price) < 0 || Number.isNaN(Number(values.regular_price))) {
      nextErrors.regular_price = t.articleListPriceRequired;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const categoryIds = values.category_id ? [values.category_id] : [];
      const commonFields: Partial<ProductFormData> = {
        name: values.name.trim(),
        sku: values.sku.trim() || undefined,
        short_description: values.short_description.trim() || undefined,
        regular_price: Number(values.regular_price),
        cost_price: values.cost_price.trim() !== '' ? Number(values.cost_price) : undefined,
        vat_rate: values.vat_rate,
        main_image_url: values.main_image_url.trim() || undefined,
        category_ids: categoryIds,
        manage_stock: warehouseActive ? values.manage_stock : (product?.manage_stock ?? false),
        stock_quantity: warehouseActive ? (Number(values.stock_quantity) || 0) : (product?.stock_quantity ?? 0),
        low_stock_threshold: warehouseActive ? (Number(values.low_stock_threshold) || 0) : (product?.low_stock_threshold ?? 0),
      };

      let result: Product | null;
      if (product) {
        result = await updateProduct(product.id, commonFields);
      } else {
        const payload: ProductFormData = {
          status: 'active',
          type: 'simple',
          backorders: 'no',
          virtual: false,
          downloadable: false,
          featured: false,
          visibility: 'visible',
          tax_status: 'taxable',
          tag_ids: [],
          attributes: [],
          attributeGroups: [],
          variations: [],
          slug: slugify(values.name),
          ...commonFields,
          name: values.name.trim(),
          category_ids: categoryIds,
          stock_quantity: commonFields.stock_quantity ?? 0,
          manage_stock: commonFields.manage_stock ?? false,
          low_stock_threshold: commonFields.low_stock_threshold ?? 0,
        };
        result = await createProduct(payload);
      }

      if (!result) throw new Error('save_failed');
      toast.success(t.articleSaved);
      onSaved(result);
    } catch (err) {
      console.error('Error saving article:', err);
      toast.error(t.articleSaveError);
    } finally {
      setSubmitting(false);
    }
  };

  return { values, setField, errors, submitting, handleSubmit };
}
